
import React, { useEffect, useState } from 'react';
import PomodoroTimer from '@/components/PomodoroTimer';
import Footer from '@/components/Footer';
import FullscreenControl from '@/components/FullscreenControl';
import UnsplashAttribution from '@/components/UnsplashAttribution';
import SubpageNavigation from '@/components/SubpageNavigation';
import BackgroundDebug from '@/components/BackgroundDebug';
import BackgroundRefreshButton from '@/components/BackgroundRefreshButton';
import { usePreferredBackground } from '@/hooks/usePreferredBackground';
import GradientBackground from "@/components/GradientBackground";

// Add type guard for TimeAwareBackgroundInfo
function isTimeAwareBackgroundInfo(obj: any): obj is { timeSlot: string; animated?: boolean } {
  return obj && typeof obj === "object" && "timeSlot" in obj;
}

const Overview = () => {
  const { backgroundInfo, isLoading } = usePreferredBackground();
  const [showDebug, setShowDebug] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [backgroundStyle, setBackgroundStyle] = useState<React.CSSProperties>({
    background: 'linear-gradient(to bottom right, #1a202c, #2d3748)'
  });

  // Show debug info in development or when explicitly enabled
  useEffect(() => {
    const isDev = import.meta.env.DEV;
    const debugParam = new URLSearchParams(window.location.search).has('debug');
    setShowDebug(isDev || debugParam);
  }, []);

  // Render webgl gradient background
  const showWebGLGradient =
    isTimeAwareBackgroundInfo(backgroundInfo) &&
    backgroundInfo.animated &&
    backgroundInfo.url === "lovable-timeofday-webgl-gradient";

  useEffect(() => {
    if (
      backgroundInfo &&
      !isLoading &&
      !showWebGLGradient // only process gradient style if not using the special webgl
    ) {
      if (
        typeof backgroundInfo.url === "string" &&
        backgroundInfo.url.startsWith('linear-gradient(')
      ) {
        setBackgroundStyle({
          background: backgroundInfo.url,
          transition: 'background 0.5s ease-in-out'
        });
      } else {
        setBackgroundStyle({
          backgroundImage: `url(${backgroundInfo.url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transition: 'background-image 0.5s ease-in-out'
        });
      }
    }
  }, [backgroundInfo, isLoading, showWebGLGradient, refreshKey]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    // Force a page reload to refresh the background
    window.location.reload();
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Animated gradient background under Pomodoro area */}
      {showWebGLGradient && isTimeAwareBackgroundInfo(backgroundInfo) && (
        <GradientBackground
          timeSlot={backgroundInfo.timeSlot as any}
        />
      )}

      <div className="visually-hidden seo-content">
        <h2>Free Pomodoro Timer – Focus Better, Work Smarter</h2>
        <p>This Free Pomodoro Timer is a simple, distraction-free online tool designed to help you stay focused, manage your time, and get more done using the proven Pomodoro Technique.</p>
      </div>
      
      <div 
        className="relative" 
        style={!showWebGLGradient ? backgroundStyle : {}}
        aria-label="Pomodoro Timer with daily background image"
        id="timer-background"
      >
        <PomodoroTimer />
        {backgroundInfo && (
          <UnsplashAttribution 
            photographerName={backgroundInfo.photographer}
            photographerUrl={backgroundInfo.photographerUrl}
            location={backgroundInfo.location}
          />
        )}
      </div>
      
      <div className="bg-background">
        <div className="container mx-auto px-4">
          <div className="text-left px-6 py-12 max-w-4xl mx-auto">
            <SubpageNavigation />
            
            <h2 className="text-3xl font-bold mb-6 text-gray-800 dark:text-gray-100">Free Pomodoro Timer – Focus Better, Work Smarter</h2>
            <p className="mb-6 text-gray-700 dark:text-gray-200">
              This Free Pomodoro Timer is a simple, distraction-free online tool designed to help you stay focused, 
              manage your time, and get more done using the proven Pomodoro Technique.
            </p>
            <p className="mb-8 text-gray-700 dark:text-gray-200">
              The Pomodoro Technique breaks your work into short, focused intervals—usually 25 minutes—called "Pomodoros," 
              followed by a 5-minute break. After every four Pomodoros, you take a longer break of 15 to 30 minutes. 
              This rhythm helps maintain concentration, reduce fatigue, and make consistent progress on tasks.
            </p>
            <p className="mb-8 text-gray-700 dark:text-gray-200">
              Whether you're studying, coding, writing, or working remotely, this timer helps you build momentum, 
              stay accountable, and beat procrastination—all without needing to sign up or download anything.
            </p>
            
            <h3 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">How to Use This Free Pomodoro Timer</h3>
            <ol className="list-decimal pl-6 mb-8 text-gray-700 dark:text-gray-200">
              <li className="mb-2">Click Start to begin your 25-minute work session.</li>
              <li className="mb-2">Focus completely on one task until the timer rings.</li>
              <li className="mb-2">Take a 5-minute break—walk around, breathe, relax.</li>
              <li className="mb-2">Repeat the cycle. After 4 Pomodoros, take a longer break of 15–30 minutes.</li>
              <li className="mb-2">Continue as needed to stay productive without burning out.</li>
            </ol>
            <p className="mb-8 text-gray-700 dark:text-gray-200">
              This timer works instantly in any modern browser—no setup, no distractions.
            </p>
            
            <h3 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">Frequently Asked Questions (FAQs)</h3>
            <div className="space-y-6 mb-8">
              <div>
                <h4 className="font-bold mb-2 text-gray-800 dark:text-gray-100">What is a Pomodoro?</h4>
                <p className="text-gray-700 dark:text-gray-200">A Pomodoro is a 25-minute focused work session followed by a short break. It's the building block of the Pomodoro Technique, a time management method designed to improve focus and productivity.</p>
              </div>
              <div>
                <h4 className="font-bold mb-2 text-gray-800 dark:text-gray-100">Is this Pomodoro timer really free?</h4>
                <p className="text-gray-700 dark:text-gray-200">Yes! This tool is 100% free to use. There are no ads, no accounts, and no hidden features.</p>
              </div>
              <div>
                <h4 className="font-bold mb-2 text-gray-800 dark:text-gray-100">Do I need to download anything?</h4>
                <p className="text-gray-700 dark:text-gray-200">No. This is a web-based timer that works instantly in your browser on desktop or mobile.</p>
              </div>
              <div>
                <h4 className="font-bold mb-2 text-gray-800 dark:text-gray-100">Can I customize the session length?</h4>
                <p className="text-gray-700 dark:text-gray-200">Yes. You can adjust the length of your work sessions, short breaks, and long breaks to match your personal productivity style.</p>
              </div>
              <div>
                <h4 className="font-bold mb-2 text-gray-800 dark:text-gray-100">Can I use this Pomodoro timer on my phone?</h4>
                <p className="text-gray-700 dark:text-gray-200">Yes. The timer is fully mobile-friendly and works on any modern smartphone or tablet.</p>
              </div>
              <div>
                <h4 className="font-bold mb-2 text-gray-800 dark:text-gray-100">How many Pomodoros should I aim for each day?</h4>
                <p className="text-gray-700 dark:text-gray-200">It depends on your workload, but most people aim for 8 to 12 Pomodoros a day. That's about 4 to 6 hours of focused work with built-in breaks.</p>
              </div>
              <div>
                <h4 className="font-bold mb-2 text-gray-800 dark:text-gray-100">Does this timer save my progress?</h4>
                <p className="text-gray-700 dark:text-gray-200">No. This tool is privacy-friendly and doesn't store your data or activity. If you need tracking or history, you can do that manually or use a journal alongside it.</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
      <FullscreenControl />
      
      {/* Debug and refresh components */}
      <BackgroundDebug isVisible={showDebug} />
      <BackgroundRefreshButton 
        onRefresh={handleRefresh}
        isVisible={showDebug}
      />

      <style>
        {`
          .glass-panel {
            border-radius: 16px !important;
            backdrop-filter: blur(10px);
            background: rgba(0, 0, 0, 0.3);
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
            transition: all 0.3s ease;
          }
          .nav-bar img {
            text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
            filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.7));
          }
          .seo-content {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border-width: 0;
          }
        `}
      </style>
    </div>
  );
};

export default Overview;
